import type { AuditListItem } from '@clockwise/clients/audit';
import { ApiError } from '@clockwise/shared';
import { DateRangeFilter, type DateRangeValue, resolveDateRange } from '@clockwise/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Filter } from 'lucide-react';
import { useState } from 'react';
import { EntityPicker } from '@/components/custom/entity-picker';
import { userPickerConfig } from '@/components/custom/entity-pickers';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { UserRow } from '@/components/custom/user-row';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { auditApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { ApprovalTimeline } from './approval-timeline';
import { AuditActor, DiffTable, OperationBadge } from './audit-diff';
import {
  AUDIT_APPROVAL_OBJECT_TYPE,
  AUDIT_ENTITY_TYPE_OPTIONS,
  entityTypeLabel,
} from './entity-types';

/** Filter state owned by the host (URL on the platform screen, local in an embed). */
export interface AuditFilters {
  entityType?: string;
  actorId?: string;
  range: DateRangeValue;
  page: number;
}

interface AuditLogViewProps {
  filters: AuditFilters;
  onChange: (next: Partial<AuditFilters>) => void;
  /**
   * Embed mode: lock the listing to a single object's history. Hides the
   * object-type filter and scopes every query to this entity.
   */
  scope?: { entityType: string; entityId: string };
  pageSize?: number;
}

const ALL_TYPES = '__all__';

/** Inclusive day bounds → ISO timestamps for the occurredAt range filter. */
function rangeToIso(range: DateRangeValue): { from?: string; to?: string } {
  const resolved = resolveDateRange(range);
  if (!resolved) return {};
  return { from: `${resolved.from}T00:00:00.000Z`, to: `${resolved.to}T23:59:59.999Z` };
}

/**
 * The platform-wide Audit Log table + filters, also embeddable on a single
 * object's screen (pass `scope`). Server-paginated; rows expand to a
 * field-level diff and — for timesheets — the approval timeline.
 */
export function AuditLogView({ filters, onChange, scope, pageSize = 50 }: AuditLogViewProps) {
  const { from, to } = rangeToIso(filters.range);
  const page = filters.page;

  const listQuery = {
    entityType: scope?.entityType ?? filters.entityType,
    entityId: scope?.entityId,
    actorId: filters.actorId,
    from,
    to,
    limit: pageSize,
    offset: page * pageSize,
  };

  const query = useQuery({
    queryKey: queryKeys.audit.list(listQuery),
    queryFn: () => auditApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * pageSize + 1;
  const showingTo = Math.min(total, page * pageSize + rows.length);

  const hasActiveFilter = Boolean(
    filters.entityType || filters.actorId || resolveDateRange(filters.range),
  );

  // Chevron + When + [Object] + Who + Action + Change.
  const columnCount = scope ? 5 : 6;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-4 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        {!scope ? (
          <div className="grid gap-1.5">
            <Select
              value={filters.entityType ?? ALL_TYPES}
              onValueChange={(v) =>
                onChange({ entityType: v === ALL_TYPES ? undefined : v, page: 0 })
              }
            >
              <SelectTrigger id="audit-type" aria-label="Object type" className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_TYPES}>All types</SelectItem>
                {AUDIT_ENTITY_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="grid gap-1.5">
          <EntityPicker
            id="audit-employee"
            config={userPickerConfig}
            value={filters.actorId ?? null}
            onChange={(id) => onChange({ actorId: id ?? undefined, page: 0 })}
            placeholder="All employees"
            searchPlaceholder="Search employees…"
            popoverWidth="auto"
            renderItem={(u, isSelected) => <UserRow user={u} isSelected={isSelected} />}
            className="w-56"
          />
        </div>

        <DateRangeFilter
          id="audit-range"
          label={null}
          value={filters.range}
          onChange={(range) => onChange({ range, page: 0 })}
          layout="inline"
        />
      </div>

      <StickyTableScroll>
        {/* Fill the container and let the flexible "Change" column absorb slack;
            below the per-column min floor (~880px with Object, ~720px when
            scoped) the container scrolls. table-fixed keeps widths content-
            independent (stable across pages); long cell values wrap rather than
            forcing the table wider — see the whitespace-normal cells below. */}
        <Table className="w-full table-fixed" style={{ minWidth: scope ? 720 : 880 }}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead className="w-44">When</TableHead>
              {!scope ? <TableHead className="w-40">Object</TableHead> : null}
              <TableHead className="w-48">Who</TableHead>
              <TableHead className="w-24">Action</TableHead>
              <TableHead>Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {Array.from({ length: columnCount }).map((_c, j) => (
                    <TableCell key={`skel-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="py-10 text-center text-destructive">
                  {query.error instanceof ApiError
                    ? `${query.error.message}${query.error.traceId ? ` (trace ${query.error.traceId})` : ''}`
                    : 'Failed to load the audit log.'}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="py-10 text-center text-muted-foreground"
                >
                  {hasActiveFilter
                    ? 'No audit records match these filters.'
                    : 'No audit records yet.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <AuditRow key={row.id} entry={row} showObject={!scope} columnCount={columnCount} />
              ))
            )}
          </TableBody>
        </Table>
      </StickyTableScroll>

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onChange({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onChange({ page: page + 1 })}
            disabled={(page + 1) * pageSize >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>
    </div>
  );
}

// Object labels for dated entities read "<name> · YYYY-MM-DD" (e.g. a
// timesheet). In the narrow Object column the name should still wrap at
// spaces, but the ISO date must stay on one line — otherwise normal
// line-breaking splits it at a hyphen ("2026-\n06-19"). Pin just the trailing
// date in a nowrap span; labels without a trailing date render unchanged.
const TRAILING_ISO_DATE = /^(.*\s)(\d{4}-\d{2}-\d{2})$/;
function ObjectLabel({ label }: { label: string }) {
  const match = TRAILING_ISO_DATE.exec(label);
  if (!match) return <>{label}</>;
  return (
    <>
      {match[1]}
      <span className="whitespace-nowrap">{match[2]}</span>
    </>
  );
}

function AuditRow({
  entry,
  showObject,
  columnCount,
}: {
  entry: AuditListItem;
  showObject: boolean;
  columnCount: number;
}) {
  const [open, setOpen] = useState(false);
  const approvalObjectType = AUDIT_APPROVAL_OBJECT_TYPE[entry.entityType];

  return (
    <>
      {/* align-top so a wrapped Change/Object/Who cell stays aligned with its
          single-line neighbours; the flexible text columns use whitespace-normal
          (overriding the shadcn default nowrap) so they wrap instead of forcing
          the table wider. */}
      <TableRow className="cursor-pointer [&>td]:align-top" onClick={() => setOpen((v) => !v)}>
        <TableCell>
          {open ? (
            <ChevronDown className="text-muted-foreground" />
          ) : (
            <ChevronRight className="text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
          {new Date(entry.occurredAt).toLocaleString()}
        </TableCell>
        {showObject ? (
          <TableCell className="whitespace-normal break-words">
            <span className="text-xs text-muted-foreground">
              {entityTypeLabel(entry.entityType)}
            </span>
            <div className="text-sm">
              {entry.label ? (
                <ObjectLabel label={entry.label} />
              ) : (
                <span className="font-mono text-xs text-muted-foreground">
                  #{entry.entityId.slice(0, 8)}
                </span>
              )}
            </div>
          </TableCell>
        ) : null}
        <TableCell className="text-sm whitespace-normal break-words">
          <AuditActor entry={entry} />
        </TableCell>
        <TableCell>
          <OperationBadge operation={entry.operation} />
        </TableCell>
        <TableCell className="text-sm whitespace-normal break-words">{entry.summary}</TableCell>
      </TableRow>
      {open ? (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={columnCount} className="py-2">
            <DiffTable changes={entry.changes} operation={entry.operation} />
            {approvalObjectType ? (
              <ApprovalTimeline objectType={approvalObjectType} objectId={entry.entityId} />
            ) : null}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
